import { useContext } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import type { TIeForm } from '@/components/App/Leasing/types';
import {
  DocumentController,
  FileDownload,
  FileUpload
} from '@/components/Shared/ui';
import { useAppSelector } from '@/store';
import type { FileType } from '@/types/uploadFiles';

import { CreateTabContext } from '../../../IeOrder';

const Documents: React.FC = () => {
  const { isFieldsFrozen } = useContext(CreateTabContext);
  const { control } = useFormContext<TIeForm>();
  const { mappedFiles } = useAppSelector((state) => state.ieLeasingOrder);

  const [documentType, proofInCome, docPhoto] = useWatch({
    control,
    name: ['documentType', 'proofInCome', 'docPhoto']
  });

  const initialDocPhoto = mappedFiles.docPhoto;

  return (
    <>
      <DocumentController
        control={control}
        name="docPhoto"
        title={
          documentType === '1'
            ? 'Фото паспорта'
            : documentType === '2'
              ? 'Фото удостоверения личности'
              : documentType === '3'
                ? 'Фото вида на жительство'
                : 'Фото документа'
        }
        maxFileSizeMb={10}
        fileLink={
          (initialDocPhoto &&
            'uuid' in initialDocPhoto &&
            initialDocPhoto?.uuid) ||
          ''
        }
        file={docPhoto}
        downloadable={!!initialDocPhoto}
        uploadable={!isFieldsFrozen}
        isRequired
      />

      {proofInCome && (
        <Controller
          control={control}
          name="incomeCert"
          rules={{
            required: 'Пожалуйста, выберите файл',
            validate: (value) => {
              if (!value || !value.name) return 'Пожалуйста, выберите файл';
              if ('size' in value && (value?.size || 0) > 10_000_000)
                return 'Файл не должен превышать 10 МБ';
            }
          }}
          render={({ field, fieldState: { error } }) =>
            !isFieldsFrozen ? (
              <FileUpload
                title="Выписка доходов за 6 месяцев"
                file={field.value as FileType}
                onChange={field.onChange}
                onDelete={() => field.onChange(null)}
                helperText="Максимальный размер файла 10 МБ"
                error={error?.message}
                isRequired
                accept=".pdf"
                allowedExtensions={['pdf']}
              />
            ) : (
              <FileDownload
                fileLink={
                  (field.value && 'uuid' in field.value && field.value?.uuid) ||
                  ''
                }
                prevStateTitle="Выписка доходов за 6 месяцев"
              />
            )
          }
        />
      )}

      <Controller
        control={control}
        name="noticeActivity"
        rules={{
          required: 'Пожалуйста, выберите файл',
          validate: (value) => {
            if (!value || !value.name) return 'Пожалуйста, выберите файл';
            if ('size' in value && (value?.size || 0) > 10_000_000)
              return 'Файл не должен превышать 10 МБ';
          }
        }}
        render={({ field, fieldState: { error } }) =>
          !isFieldsFrozen ? (
            <FileUpload
              title="Уведомление о деятельности в качестве ИП"
              file={field.value as FileType}
              onChange={field.onChange}
              onDelete={() => field.onChange(null)}
              helperText="Максимальный размер файла 10 МБ"
              error={error?.message}
              isRequired
              accept=".pdf"
              allowedExtensions={['pdf']}
            />
          ) : (
            <FileDownload
              fileLink={
                (field.value && 'uuid' in field.value && field.value?.uuid) ||
                ''
              }
              prevStateTitle="Уведомление о деятельности в качестве ИП"
            />
          )
        }
      />

      <Controller
        control={control}
        name="otherDoc"
        rules={{
          required: 'Пожалуйста, выберите файл',
          validate: (value) => {
            if (!value || !value.name) return 'Пожалуйста, выберите файл';
            if ('size' in value && (value?.size || 0) > 10_000_000)
              return 'Файл не должен превышать 10 МБ';
          }
        }}
        render={({ field, fieldState: { error } }) =>
          !isFieldsFrozen ? (
            <FileUpload
              title="Справка о доходах/выписка (с QR)"
              file={field.value as FileType}
              onChange={field.onChange}
              onDelete={() => field.onChange(null)}
              helperText="Максимальный размер файла 10 МБ"
              error={error?.message}
              isRequired
              accept=".pdf"
              allowedExtensions={['pdf']}
            />
          ) : (
            <FileDownload
              fileLink={
                (field.value && 'uuid' in field.value && field.value?.uuid) ||
                ''
              }
              prevStateTitle="Справка о доходах/выписка (с QR)"
            />
          )
        }
      />
    </>
  );
};

export default Documents;
